import React, { useEffect, useState } from "react";
import { Box, Flex, Grid } from "theme-ui";
import { getAdminStats } from "api/QuoteExpiration";
import { themevals } from "theme/themevals";

const REASON_LABELS: Record<string, string> = {
  lost_to_competitor: "Lost to competitor",
  price_too_high: "Price too high",
  budget_cut: "Budget cut",
  project_cancelled: "Project cancelled",
  timeline_pushed: "Timeline pushed",
  scope_changed: "Scope changed",
  client_unresponsive: "Client unresponsive",
  just_budgeting: "Just budgeting",
  project_not_awarded: "Project not awarded",
  went_different_direction: "Different direction",
  other: "Other",
};

const StatCard: React.FC<{
  label: string;
  value: number;
  color?: string;
  sub?: string;
}> = ({ label, value, color, sub }) => (
  <Box
    sx={{
      padding: "16px",
      borderRadius: "6px",
      border: "1px solid " + themevals.colors.inputOutline,
      backgroundColor: "#FFFFFF",
      textAlign: "center",
    }}
  >
    <Box
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "28px",
        fontWeight: 700,
        color: color || "base_800",
        lineHeight: 1.2,
      }}
    >
      {value}
    </Box>
    <Box
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "12px",
        color: "base_500",
        marginTop: "4px",
      }}
    >
      {label}
    </Box>
    {sub && (
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "11px", color: "base_400", marginTop: "2px" }}>
        {sub}
      </Box>
    )}
  </Box>
);

const BarSegment: React.FC<{
  label: string;
  value: number;
  total: number;
  color: string;
}> = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Flex sx={{ alignItems: "center", gap: "8px", marginBottom: "8px" }}>
      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500", width: "100px", flexShrink: 0 }}>{label}</Box>
      <Box sx={{ flex: 1, backgroundColor: "#F0F0F0", borderRadius: "4px", height: "20px", overflow: "hidden" }}>
        <Box
          sx={{
            width: pct + "%",
            minWidth: value > 0 ? "4px" : "0",
            height: "100%",
            backgroundColor: color,
            borderRadius: "4px",
            transition: "width 0.3s ease",
          }}
        />
      </Box>
      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 600, color: "base_700", width: "40px", textAlign: "right", flexShrink: 0 }}>
        {value}
      </Box>
    </Flex>
  );
};

const ExpirationStatsWidget: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const data = await getAdminStats();
      if (data?.expiringQuotes) {
        setStats(data);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          marginBottom: "24px",
          padding: "24px",
          minHeight: "200px",
        }}
      >
        <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: "base_800" }}>
          Expiration Intelligence
        </Box>
        <Box sx={{ padding: "40px 0", textAlign: "center" }}>
          <Box sx={{ backgroundColor: "#F5F5F5", height: "80px", borderRadius: "4px" }} />
        </Box>
      </Box>
    );
  }

  if (!stats) return null;

  const { expiringQuotes, expiringRfqs, recentOutcomes, declineReasons } = stats;
  const totalExpiring = (expiringQuotes?.within30 || 0) + (expiringRfqs?.within30 || 0);

  // Don't render if no data at all
  if (totalExpiring === 0 && (recentOutcomes?.total || 0) === 0 && (!declineReasons || declineReasons.length === 0)) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        marginBottom: "24px",
        padding: "24px",
      }}
    >
      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: "base_800", marginBottom: "20px" }}>
        Expiration Intelligence
      </Box>

      {/* Expiring counts by window */}
      <Box sx={{ marginBottom: "24px" }}>
        <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500", marginBottom: "12px" }}>
          Expiring in Next 30 Days
        </Box>
        <Grid columns={[2, 3, 6]} gap="12px">
          <StatCard label="Quotes (7d)" value={expiringQuotes?.within7 || 0} color={themevals.colors.danger} />
          <StatCard label="Quotes (14d)" value={expiringQuotes?.within14 || 0} color={themevals.colors.warning} />
          <StatCard label="Quotes (30d)" value={expiringQuotes?.within30 || 0} />
          <StatCard label="RFQs (7d)" value={expiringRfqs?.within7 || 0} color={themevals.colors.danger} />
          <StatCard label="RFQs (14d)" value={expiringRfqs?.within14 || 0} color={themevals.colors.warning} />
          <StatCard label="RFQs (30d)" value={expiringRfqs?.within30 || 0} />
        </Grid>
      </Box>

      {/* Recent outcomes + decline reasons side by side */}
      <Flex sx={{ gap: "24px", flexWrap: "wrap" }}>
        {/* Outcomes (last 30 days) */}
        {(recentOutcomes?.total || 0) > 0 && (
          <Box sx={{ flex: "1 1 300px", minWidth: "280px" }}>
            <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500", marginBottom: "12px" }}>
              Outcomes (Last 30 Days)
            </Box>
            <BarSegment label="Regenerated" value={recentOutcomes?.regenerated || 0} total={recentOutcomes?.total || 1} color={themevals.colors.primary} />
            <BarSegment label="Declined" value={recentOutcomes?.declined || 0} total={recentOutcomes?.total || 1} color={themevals.colors.warning} />
            <BarSegment label="No Action" value={recentOutcomes?.noAction || 0} total={recentOutcomes?.total || 1} color={themevals.colors.dkgrey} />
          </Box>
        )}

        {/* Decline reasons (last 90 days) */}
        {declineReasons && declineReasons.length > 0 && (
          <Box sx={{ flex: "1 1 300px", minWidth: "280px" }}>
            <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500", marginBottom: "12px" }}>
              Decline Reasons (Last 90 Days)
            </Box>
            {declineReasons.map((r: { category: string | null; count: number }, idx: number) => {
              const label = r.category ? (REASON_LABELS[r.category] || r.category) : "No reason given";
              const maxCount = declineReasons[0]?.count || 1;
              return (
                <BarSegment
                  key={r.category || "null-" + idx}
                  label={label}
                  value={r.count}
                  total={maxCount}
                  color={themevals.colors.info}
                />
              );
            })}
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default ExpirationStatsWidget;
